import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search as SearchIcon, X, Package, ShoppingBag } from "lucide-react";
import api from "@/lib/api";

export default function SearchPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const scope = ["MARKETPLACE", "STILLTOUCHEDX"].includes(params.get("scope")) ? params.get("scope") : "ALL";
  const segment = ["FEMALE", "MALE"].includes(params.get("segment")) ? params.get("segment") : "ALL";

  const SCOPE_META = {
    ALL: { title: t("search.title_all"), placeholder: t("search.placeholder_all"), empty: t("search.empty_all"), exitPath: "/" },
    MARKETPLACE: { title: t("search.title_marketplace"), placeholder: t("search.placeholder_marketplace"), empty: t("search.empty_marketplace"), exitPath: "/marketplace" },
    STILLTOUCHEDX: { title: t("search.title_stx"), placeholder: t("search.placeholder_stx"), empty: t("search.empty_stx"), exitPath: "/stilltouchedx" },
  };
  const SEGMENT_LABEL = { ALL: t("search.seg_all"), FEMALE: t("search.seg_female"), MALE: t("search.seg_male") };

  const meta = SCOPE_META[scope];
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const setSegment = (seg) => {
    const next = new URLSearchParams(params);
    if (seg === "ALL") next.delete("segment"); else next.set("segment", seg);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (q.length < 2) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}&scope=${scope}&segment=${segment}`);
        setResults(data);
      } catch { setResults({ profiles: [], posts: [], listings: [] }); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, scope, segment]);

  const listingRoute = (l) => (scope === "STILLTOUCHEDX" || l.area_scope === "STILLTOUCHEDX")
    ? `/stx/${l.listing_id}` : `/marketplace/${l.listing_id}`;

  return (
    <div data-testid="search-page" data-scope={scope}>
      <div className="sticky top-0 z-40 bg-white px-4 py-3 border-b border-[#E5E5E5]">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-[#666] uppercase tracking-wider">{meta.title}</p>
          <button
            data-testid="search-exit"
            aria-label={t("search.close")}
            onClick={() => navigate(meta.exitPath)}
            className="w-8 h-8 flex items-center justify-center hover:bg-[#F5F5F5]">
            <X size={16} className="text-[#666]" />
          </button>
        </div>
        <div className="relative">
          <SearchIcon size={16} className="absolute left-3 top-3 text-[#999]" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-10 pl-9 pr-9 bg-[#F5F5F5] text-sm text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            placeholder={meta.placeholder}
            autoFocus
          />
          {q && (
            <button data-testid="search-clear" onClick={() => { setQ(""); setResults(null); }} className="absolute right-3 top-3 text-[#999]">
              <X size={16} />
            </button>
          )}
        </div>
        {/* iter79 Batch 3: Segment-Filter Damen/Herren — nur MP + STX */}
        {(scope === "MARKETPLACE" || scope === "STILLTOUCHEDX") && (
          <div data-testid="search-segment-chips" className="flex gap-2 mt-3">
            {["ALL", "FEMALE", "MALE"].map((seg) => (
              <button
                key={seg}
                data-testid={`search-segment-${seg}`}
                onClick={() => setSegment(seg)}
                className={`px-3 h-8 text-xs font-medium border transition-colors ${
                  segment === seg
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-[#666] border-[#E5E5E5] hover:border-[#0A0A0A]"
                }`}
              >
                {SEGMENT_LABEL[seg]}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
        </div>
      )}

      {results && !loading && (
        <div>
          {/* Listings (MP + STX) */}
          {results.listings?.length > 0 && (
            <section data-testid="search-listings-section" className="border-b border-[#E5E5E5]">
              <h3 className="px-4 py-2 text-xs font-bold text-[#999] uppercase tracking-wider">
                {scope === "MARKETPLACE" ? t("search.section_items_mp") : scope === "STILLTOUCHEDX" ? t("search.section_items_stx") : t("search.section_items")}
              </h3>
              <div className="grid grid-cols-2 gap-2 p-2">
                {results.listings.map((l) => (
                  <button
                    key={l.listing_id}
                    data-testid={`search-listing-${l.listing_id}`}
                    onClick={() => navigate(listingRoute(l))}
                    className="text-left bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
                  >
                    <div className="aspect-square bg-[#F5F5F5] flex items-center justify-center overflow-hidden">
                      {l.media_urls?.[0] ? (
                        <img src={l.media_urls[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Package size={20} className="text-[#CCC]" />
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-[#0A0A0A] truncate">{l.title}</p>
                      <p className="text-xs font-medium text-[#0A0A0A] mt-0.5">{Number(l.price || 0).toFixed(2)} {l.currency || "EUR"}</p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Profiles (STX-Seller oder ALL) */}
          {results.profiles?.length > 0 && (
            <section data-testid="search-profiles-section" className="border-b border-[#E5E5E5]">
              <h3 className="px-4 py-2 text-xs font-bold text-[#999] uppercase tracking-wider">
                {scope === "STILLTOUCHEDX" ? t("search.section_sellers") : t("search.section_profiles")}
              </h3>
              {results.profiles.map((p) => (
                <button
                  key={p.profile_id}
                  data-testid={`search-profile-${p.handle}`}
                  onClick={() => navigate(`/user/${p.handle}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFA] transition-colors"
                >
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#E5E5E5] flex items-center justify-center text-sm font-bold text-[#999]">
                      {(p.display_name || "?")[0]}
                    </div>
                  )}
                  <div className="text-left">
                    <p className="text-sm font-semibold text-[#0A0A0A]">{p.display_name}</p>
                    {p.bio && <p className="text-xs text-[#999] truncate">{p.bio}</p>}
                  </div>
                </button>
              ))}
            </section>
          )}

          {/* Posts (nur ALL-Scope) */}
          {results.posts?.length > 0 && (
            <section data-testid="search-posts-section">
              <h3 className="px-4 py-2 text-xs font-bold text-[#999] uppercase tracking-wider">{t("search.section_posts")}</h3>
              {results.posts.map((p) => (
                <div key={p.post_id} data-testid={`search-post-${p.post_id}`} className="px-4 py-3 border-b border-[#E5E5E5]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-[#0A0A0A]">{p.author?.display_name || "Unknown"}</span>
                  </div>
                  <p className="text-sm text-[#666]">{p.text}</p>
                </div>
              ))}
            </section>
          )}

          {(results.listings?.length ?? 0) === 0 && (results.profiles?.length ?? 0) === 0 && (results.posts?.length ?? 0) === 0 && (
            <div data-testid="search-empty" className="px-4 py-12 text-center">
              <ShoppingBag size={28} className="mx-auto text-[#CCC] mb-2" />
              <p className="text-sm text-[#999]">{t("search.no_results", { q })}</p>
            </div>
          )}
        </div>
      )}

      {!results && !loading && (
        <div className="px-4 py-12 text-center text-sm text-[#999]">{meta.empty}</div>
      )}
    </div>
  );
}
